<script setup lang="ts">
import { enUS } from 'date-fns/locale'
import { VueDatePicker as Datepicker } from '@vuepic/vue-datepicker'

import { faCalendar } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { ModalDefault } from '@shared/ui/modals'
import { LinkTextPrimary } from '@shared/ui/links'
import { AppIcon } from '@shared/ui/icons'
import { InputPrimary } from '@shared/ui/fields'
import { BtnPrimary, BtnSecondary } from '@shared/ui/buttons'

import { useModalEditApiToken } from '../../../model/api-tokens/modals/modal-edit-api-token.hook'

defineOptions({
  name: 'ModalEditApiToken',
})

const {
  refIsOpen,
  refIsLoading,
  refToken,
  refTitle,
  refName,
  refExpiresAt,
  refMinExpiresDate,
  refIsValid,
  getUserHref,
  formatDateTime,
  setName,
  closeModal,
  saveToken,
} = useModalEditApiToken()
</script>

<template>
  <ModalDefault
    :show="refIsOpen"
    :loading="refIsLoading"
    :title="refTitle"
    is-close-button
    @click:overlay="closeModal"
    @click:close="closeModal"
  >
    <div class="modal-edit-api-token">
      <InputPrimary
        id="api-token-edit-name"
        label="Name"
        required
        placeholder="Token name"
        :value="refName"
        @update:value="setName"
      />

      <div class="modal-edit-api-token__field">
        <AppText
          name="body2"
          color="slate-900"
          class="modal-edit-api-token__label"
        >
          Expires at
        </AppText>
        <Datepicker
          v-model="refExpiresAt"
          :locale="enUS"
          :week-start="1"
          :formats="{ input: 'yyyy-MM-dd' }"
          :time-config="{ enableTimePicker: false }"
          :min-date="refMinExpiresDate"
          auto-apply
          clearable
          placeholder="Never"
          teleport
        >
          <template #input-icon>
            <AppIcon :icon="faCalendar" />
          </template>
        </Datepicker>
      </div>

      <div
        v-if="refToken"
        class="modal-edit-api-token__field"
      >
        <AppText
          name="body2"
          color="slate-500"
        >
          User
        </AppText>
        <LinkTextPrimary
          v-if="refToken.user"
          :href="getUserHref(refToken)"
          target="_blank"
        >
          {{ refToken.user.name }}
        </LinkTextPrimary>
        <AppText
          v-else
          name="body1"
          color="slate-900"
        >
          —
        </AppText>
      </div>

      <div
        v-if="refToken"
        class="modal-edit-api-token__field"
      >
        <AppText
          name="body2"
          color="slate-500"
        >
          Last used
        </AppText>
        <AppText
          name="body1"
          color="slate-900"
        >
          {{ formatDateTime(refToken.last_used_at) }}
        </AppText>
      </div>

      <div
        v-if="refToken"
        class="modal-edit-api-token__field"
      >
        <AppText
          name="body2"
          color="slate-500"
        >
          Created
        </AppText>
        <AppText
          name="body1"
          color="slate-900"
        >
          {{ formatDateTime(refToken.created_at) }}
        </AppText>
      </div>
    </div>

    <template #controls>
      <BtnPrimary
        text="Save"
        color="emerald"
        :disabled="!refIsValid || refIsLoading"
        @click="saveToken"
      />
      <BtnSecondary
        text="Cancel"
        @click="closeModal"
      />
    </template>
  </ModalDefault>
</template>

<style scoped lang="scss" src="./ModalEditApiToken.scss" />
